import _ from "lodash";
import { StyleSheet, Text, View } from "@react-pdf/renderer";
import React from "react";
import { formatCurrencyBlankUSD, formatPercentageBlank, formatString } from "./formattingUtils";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const PDFJS = require("pdfjs-dist/webpack");

export const parseJSON = data => JSON.parse(JSON.stringify(data));

export const currentYear = new Date().getFullYear();
const startYear = 1950;
const styles = StyleSheet.create({
    footer: {
        position: "absolute",
        left: 50,
        right: 50,
        fontSize: 9,
        textAlign: "center",
        borderTop: "1px solid #ecf0f1",
        paddingTop: 6,
        color: "#7f8c8d",
        bottom: 15,
    },
});
const PAGE_DIMENSIONS = {
    A4_WIDTH: 595.28,
    A4_HEIGHT: 841.89,
    PADDING: 40,
    MARGIN: 20,
    FOOTER_HEIGHT: 60,
};

interface BuildingArea {
    Total: number;
}

interface SiteArea {
    acres: number;
    sqFt: number;
}

export const yearBuiltOptions = Array.from({ length: currentYear - startYear + 1 }, (_, index) => {
    const year = currentYear - index;

    return { value: year.toString(), label: year.toString() };
});

export const getSalientFactsTableConfig = {
    salientFacts: [
        {
            label: "County",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.county",
            format: (value: string) => value,
        },
        {
            label: "Date of Value",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.dateOfValue",
            format: (value: string) => value,
        },
        {
            label: "Taxes Payable Year",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.taxesPayableYear",
            format: (value: string) => value,
        },
        {
            label: "Property Owner",
            key: "appealApproachCalculationData.exportAppealsPackage.coverPage.subjectPropertyOwner",
            format: (value: string) => value || "N/A",
        },
        {
            label: "Property Name",
            key: "appealApproachCalculationData.exportAppealsPackage.coverPage.subjectPropertyNumber",
            format: (value: string) => value,
        },
        {
            label: "Address",
            key: "appealApproachCalculationData.exportAppealsPackage.coverPage.subjectPropertyAddress.streetaddress",
            format: (value: string) => value,
        },
        {
            label: "City",
            key: "appealApproachCalculationData.exportAppealsPackage.coverPage.subjectPropertyAddress.city",
            format: (value: string) => value,
        },
        {
            label: "State",
            key: "appealApproachCalculationData.exportAppealsPackage.coverPage.subjectPropertyAddress.state",
            format: (value: string) => value,
        },
        {
            label: "Zip Code",
            key: "appealApproachCalculationData.exportAppealsPackage.coverPage.subjectPropertyAddress.zipCode",
            format: (value: string | number) => value.toString(),
        },
        {
            label: "Property Type",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.propertyType",
            format: (value: string) => value,
        },
        {
            label: "Number of Buildings",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.numberOfBuildings",
            format: (value: string) => value || "1",
        },
        {
            label: "Building Area (SF)",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.buildingArea",
            format: (value: BuildingArea) =>
                `${Object.entries(value)
                    .map(([key, val]) => `${key}: ${val}`)
                    .join(" ")}`,
        },
        {
            label: "Year Built",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.yearBuilt",
            format: (value: string) => value,
        },
        {
            label: "Site Area (SF)",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.siteArea",
            format: (value: SiteArea) => `${value.sqFt} SqFt     ${value.acres.toFixed(2)} Acres`,
        },
        {
            label: "Buildable (SF)",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.buildable",
            format: (value: string) => value || "N/A",
        },
        {
            label: "Parcel Number(s)",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.parcelNumber",
            format: (value: string) => value,
        },
        {
            label: "Jurisdiction",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.jurisdiction",
            format: (value: string) => value,
        },
        {
            label: "Reference Number",
            key: "appealApproachCalculationData.exportAppealsPackage.salientFacts.referenceNumber",
            format: (value: string) => value || "N/A",
        },
    ],

    valueBasedTable: {
        headers: [
            "Parcel(s)",
            "Land Size",
            "Building Size",
            "Land",
            "$/SF",
            "Improvements",
            "$/SF",
            "Total",
            "$/SF",
        ],
    },

    marketValueIndication: {
        headers: ["Type", "Land", "Buildable $/SF", "Improvements", "$/SF", "Total", "$/SF"],
        rows: [
            {
                type: "Replacement Cost",
                getValue: (data: any) => {
                    const values = _.get(
                        data,
                        "appealApproachCalculationData.exportAppealsPackage.marketValueIndication.replacementCost",
                        {}
                    );

                    return {
                        land: values.land || 0,
                        improvements: values.improvements || 0,
                        total: values.total || 0,
                        landValuePerSqFt: values.landValuePerSqFt || 0,
                        improvementsPerSqFt: values.improvementsPerSqFt || 0,
                        totalPerSqFt: values.totalPerSqFt || 0,
                    };
                },
            },
            {
                type: "Comparable Sales Approach",
                getValue: (data: any) => {
                    const values = _.get(
                        data,
                        "appealApproachCalculationData.exportAppealsPackage.marketValueIndication.comparableSalesApproach",
                        {}
                    );

                    return {
                        land: values.land || 0,
                        improvements: values.improvements || 0,
                        total: values.total || 0,
                        landValuePerSqFt: values.landValuePerSqFt || 0,
                        improvementsPerSqFt: values.improvementsPerSqFt || 0,
                        totalPerSqFt: values.totalPerSqFt || 0,
                    };
                },
            },
            {
                type: "Fee Simple Income Approach",
                getValue: (data: any) => {
                    const values = _.get(
                        data,
                        "appealApproachCalculationData.exportAppealsPackage.marketValueIndication.feeSimpleIncomeApproach",
                        {}
                    );

                    return {
                        land: values.land || 0,
                        improvements: values.improvements || 0,
                        total: values.total || 0,
                        landValuePerSqFt: values.landValuePerSqFt || 0,
                        improvementsPerSqFt: values.improvementsPerSqFt || 0,
                        totalPerSqFt: values.totalPerSqFt || 0,
                    };
                },
            },
            {
                type: "Indication of Fair Market Value",
                getValue: (data: any) => {
                    const values = _.get(
                        data,
                        "appealApproachCalculationData.exportAppealsPackage.marketValueIndication.indicationOfFairMarketValue",
                        {}
                    );

                    return {
                        land: values.land || 0,
                        improvements: values.improvements || 0,
                        total: values.total || 0,
                        landValuePerSqFt: values.landValuePerSqFt || 0,
                        improvementsPerSqFt: values.improvementsPerSqFt || 0,
                        totalPerSqFt: values.totalPerSqFt || 0,
                    };
                },
            },
        ],
        formatMoney: (value: number) =>
            new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(value),
    },
};

interface ImageDimensions {
    width: number;
    height: number;
}

export const calculateImageDimensions = (
    imageWidth: number,
    imageHeight: number
): ImageDimensions => {
    const maxWidth = PAGE_DIMENSIONS.A4_WIDTH - PAGE_DIMENSIONS.PADDING;
    const maxHeight =
        PAGE_DIMENSIONS.A4_HEIGHT - PAGE_DIMENSIONS.MARGIN - PAGE_DIMENSIONS.FOOTER_HEIGHT;

    const widthRatio = maxWidth / imageWidth;
    const heightRatio = maxHeight / imageHeight;
    const scale = Math.min(widthRatio, heightRatio);

    return {
        width: imageWidth * scale,
        height: imageHeight * scale,
    };
};

export interface ConvertedImage {
    url: string;
    width: number;
    height: number;
}

export const convertPdfToImages = async (pdfUrl: string): Promise<ConvertedImage[]> => {
    try {
        const response = await fetch(pdfUrl);
        const pdfData = new Uint8Array(await response.arrayBuffer());
        const pdf = await PDFJS.getDocument({ data: pdfData }).promise;
        const canvas = document.createElement("canvas");
        const scale = 2;
        const images: ConvertedImage[] = [];

        for (let i = 0; i < pdf.numPages; i++) {
            const page = await pdf.getPage(i + 1);
            const viewport = page.getViewport({ scale });
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            const context = canvas.getContext("2d");

            await page.render({
                canvasContext: context,
                viewport: viewport,
            }).promise;

            images.push({
                url: canvas.toDataURL("image/jpeg", 1.0),
                width: viewport.width,
                height: viewport.height,
            });
        }

        canvas.remove();

        return images;
    } catch (error) {
        console.error("Error converting PDF to images:", error);

        return [];
    }
};

export const getFooter = confidentialText => {
    return (
        <View style={styles.footer}>
            <Text>{confidentialText}</Text>
        </View>
    );
};

export const salesCompsTableConfig = {
    rowResources: {
        getRows: (data: any) => {
            const comps = _.get(data, "appealApproachCalculationData.sales.comps", {});
            const compList = Object.keys(comps)
                .map(key => comps[key])
                .filter(comp => comp.enable === true);
            const subject = _.get(data, "appealApproachCalculationData.sales.subject", {});
            const summary = _.get(data, "appealApproachCalculationData.sales.summary", {});

            const propertyName = [];
            propertyName.push("Property Name", subject.propertyNumber, ...compList.map(() => "-"));

            const propertyAddress = [];
            propertyAddress.push(
                "Property Address",
                subject.address.streetaddress,
                ...compList.map(comp => comp.address.streetaddress)
            );

            const propertyCity = [];
            propertyCity.push(
                "Property City",
                subject.address.city,
                ...compList.map(comp => comp.address.city)
            );

            const county = [];
            county.push(
                "County",
                subject.address.county,
                ...compList.map(comp => comp.address.county)
            );

            const saleDate = [];
            saleDate.push(
                "Assessment or Sale Date",
                subject.saleDate || "-",
                ...compList.map(comp => comp.saleDate || "-")
            );

            const salePrice = [];
            salePrice.push(
                "Assessor or Sale Price",
                `$${summary.indicatedValue.reconciled.toLocaleString()}`,
                ...compList.map(comp =>
                    comp.salePrice ? `$${comp.salePrice.toLocaleString()}` : "-"
                )
            );

            const buildingType = [];
            buildingType.push(
                "Building Type",
                subject.propertyType || "-",
                ...compList.map(comp => comp.propertyType || "-")
            );

            const rentalClass = [];
            rentalClass.push(
                "Rental Class",
                subject.rentalClass || "-",
                ...compList.map(comp => comp.rentalClass || "-")
            );

            const constructionClass = [];
            constructionClass.push(
                "Construction Class",
                subject.constructionClass || "-",
                ...compList.map(comp => comp.constructionClass || "-")
            );

            const clearHeight = [];
            clearHeight.push(
                "Clear Height",
                subject.averageStoryHeight ? `${subject.averageStoryHeight}'` : "-",
                ...compList.map(comp =>
                    comp.averageStoryHeight ? `${comp.averageStoryHeight}'` : "-"
                )
            );

            const yearBuilt = [];
            yearBuilt.push(
                "Year Built (Effective)",
                subject.effectiveYear || "-",
                ...compList.map(comp => comp.effectiveYear || "-")
            );

            const buildingAreaSF = [];
            buildingAreaSF.push(
                "Building AreaSF (SF)",
                subject.buildingSizeSqFt?.toLocaleString() || "-",
                ...compList.map(comp => comp.buildingSizeSqFt?.toLocaleString() || "-")
            );

            const landAreaSF = [];
            landAreaSF.push(
                "Land AreaSF (SF)",
                subject.landSizeSqFt?.toLocaleString() || "-",
                ...compList.map(comp => comp.landSizeSqFt?.toLocaleString() || "-")
            );

            const pricePerSF = [];
            pricePerSF.push(
                "$/SF",
                summary.indicatedValue.indicatedRate,
                ...compList.map(comp =>
                    comp.salesInformation.salePricePerSqFt
                        ? `$${comp.salesInformation.salePricePerSqFt.toFixed(2)}`
                        : "-"
                )
            );

            const landToBuildingRatio = [];
            landToBuildingRatio.push(
                "Land to Building Ratio",
                subject.landToBuildingRatio.toFixed(2) || "-",
                ...compList.map(comp => comp.landToBuildingRatio.toFixed(2) || "-")
            );

            const finalAssessor = [];
            finalAssessor.push(
                "Final Assessor Value/SQFT",
                summary.indicatedValue.indicatedRate,
                ...compList.map(comp =>
                    comp.salesInformation.salePricePerSqFt
                        ? `$${comp.salesInformation.salePricePerSqFt.toFixed(2)}`
                        : "-"
                )
            );

            return [
                propertyName,
                propertyAddress,
                propertyCity,
                county,
                saleDate,
                salePrice,
                buildingType,
                rentalClass,
                constructionClass,
                buildingAreaSF,
                landAreaSF,
                clearHeight,
                yearBuilt,
                pricePerSF,
                landToBuildingRatio,
            ];
        },
    },
};

export const salesAdjustmentsTableConfig = {
    rowResources: {
        getRows: (data: any) => {
            const comps = _.get(data, "appealApproachCalculationData.sales.comps", {});
            const compList = Object.keys(comps)
                .map(key => comps[key])
                .filter(comp => comp.enable === true);

            const propertyRightsConveyed = [];
            propertyRightsConveyed.push(
                "Property rights conveyed",
                ...compList.map(comp => comp.adjustments.propertyRightsConveyed.rate)
            );

            const financing = [];
            financing.push("Financing", ...compList.map(comp => comp.adjustments.financing.rate));

            const conditionsOfSale = [];
            conditionsOfSale.push(
                "Conditions Of Sale",
                ...compList.map(comp => comp.adjustments.conditionsOfSale.rate)
            );

            const expenditureAfterSale = [];
            expenditureAfterSale.push(
                "Expenditure After Sale",
                ...compList.map(comp => comp.adjustments.expenditureAfterSale.rate)
            );

            const marketConditions = [];
            marketConditions.push(
                "Market Conditions",
                ...compList.map(comp => comp.adjustments.marketConditions.rate)
            );

            const ageCondition = [];
            ageCondition.push(
                "Age/Condition",
                ...compList.map(comp => comp.otherAdjustments.ageCondition)
            );

            const grossAdjustments = [];
            grossAdjustments.push(
                "Gross Adjustments",
                ...compList.map(comp => comp.otherAdjustments.grossAdjustments)
            );

            const landToBuildingRatio = [];
            landToBuildingRatio.push(
                "Land To Building Ratio",
                ...compList.map(comp => comp.otherAdjustments.landToBuildingRatio)
            );

            const location = [];
            location.push("Location", ...compList.map(comp => comp.otherAdjustments.location));

            const officeFinishedArea = [];
            officeFinishedArea.push(
                "Office/FinishedArea",
                ...compList.map(comp => comp.otherAdjustments.officeFinishedArea)
            );

            const netAdjustments = [];
            netAdjustments.push(
                "Net Adjustments",
                ...compList.map(comp => comp.otherAdjustments.netAdjustments)
            );

            const otherAdjustments = [];
            otherAdjustments.push(
                "Other Adjustments",
                ...compList.map(comp => comp.otherAdjustments.otherAdjustments)
            );

            const qualityFunctionalUtility = [];
            qualityFunctionalUtility.push(
                "Quality/FunctionalUtility",
                ...compList.map(comp => comp.otherAdjustments.qualityFunctionalUtility)
            );

            const size = [];
            size.push("Size", ...compList.map(comp => comp.otherAdjustments.size));

            const storyHeight = [];
            storyHeight.push(
                "Story Height",
                ...compList.map(comp => comp.otherAdjustments.storyHeight)
            );

            const adjustedRate = [];
            adjustedRate.push("Adjusted Rate", ...compList.map(comp => comp.adjustedPricePerSqFt));

            return [
                propertyRightsConveyed,
                financing,
                conditionsOfSale,
                expenditureAfterSale,
                marketConditions,
                location,
                size,
                ageCondition,
                qualityFunctionalUtility,
                officeFinishedArea,
                landToBuildingRatio,
                storyHeight,
                otherAdjustments,
                netAdjustments,
                grossAdjustments,
            ];
        },
    },
};

export const salesReviewIndicationTableConfig = {
    rowResources: {
        getRows: (data: any) => {
            const summary = _.get(data, "appealApproachCalculationData.sales.summary", {});

            const salesReviewIndication = [];
            salesReviewIndication.push(
                summary.salesReviewIndication.low,
                summary.salesReviewIndication.high,
                summary.salesReviewIndication.median,
                summary.salesReviewIndication.average,
                summary.salesReviewIndication.weightedAverage
            );

            return salesReviewIndication;
        },
    },
    headers: ["Low", "High", "Median", "Average", "Weighted Average"],
};

const createTableRow = (
    label: string,
    value?: string | number,
    percentage?: string | number,
    amount?: string | number
): string[] => [
    label,
    value?.toString() || "",
    percentage?.toString() || "",
    amount?.toString() || "",
];

interface OperatingIncomeData {
    rate: number;
    size: number;
    potentialGrossIncome: number;
    lessMarketVacancy: number;
    lessCreditLossConcessions: number;
    lessLossToLease: number;
    scheduledGrossIncome: number;
    effectiveGrossIncome: number;
    camRecovery: number;
    utilityReimbursementIncome: number;
    miscellaneousOtherIncome: number;
    totalOtherIncome: number;
}

interface FixedExpensesData {
    realEstateTaxes: number;
    insurance: number;
    professionalManagement: number;
    onSiteStaffing: number;
    maintenanceStaffing: number;
    subtotal: number;
}

interface VariableExpensesData {
    parking: number;
    cleaning: number;
    repairsAndMaintenance: number;
    elevator: number;
    utilities: number;
    securityGrounds: number;
    administrationAndManagement: number;
    camExpenses: number;
    subtotal: number;
}

interface CapitalExpenditureData {
    replacementReserves: number;
    subtotal: number;
}

export interface SpaceData {
    enable: boolean;
    spaceDetails: {
        spaceType: string;
    };
    incomeExpenseProForma: {
        operatingIncome: OperatingIncomeData;
        fixedExpenses: FixedExpensesData;
        variableExpenses: VariableExpensesData;
        capitalExpenditure: CapitalExpenditureData;
        unloadedCap: number;
        etr: number;
        oar: number;
        indicatedValue: number;
        rentLossConcession: number;
        adjustedValue: number;
        totalOperatingExpenses: number;
        totalExpenseLoad: number;
        netOperatingIncome: number;
    };
}

export const operatingIncomeConfig = {
    getRows: (space: SpaceData): string[][] => {
        const { operatingIncome: oi } = space.incomeExpenseProForma;

        return [
            createTableRow(
                "Scheduled Gross Industrial Income",
                formatCurrencyBlankUSD(oi.rate),
                formatString(oi.size),
                formatCurrencyBlankUSD(oi.potentialGrossIncome)
            ),
            createTableRow(
                "Less Market Vacancy",
                "",
                formatPercentageBlank(oi.lessMarketVacancy),
                formatCurrencyBlankUSD((oi.lessMarketVacancy * oi.size) / 100)
            ),
            createTableRow(
                "Less Credit Loss/Concessions",
                "",
                formatPercentageBlank(oi.lessCreditLossConcessions),
                formatCurrencyBlankUSD((oi.lessCreditLossConcessions * oi.size) / 100)
            ),
            createTableRow(
                "Less Loss To Lease",
                "",
                formatPercentageBlank(oi.lessLossToLease),
                formatCurrencyBlankUSD((oi.lessLossToLease * oi.size) / 100)
            ),
            createTableRow(
                "Net Rental Income",
                formatCurrencyBlankUSD(oi.scheduledGrossIncome / oi.size),
                formatString(oi.size),
                formatCurrencyBlankUSD(oi.scheduledGrossIncome)
            ),
        ];
    },
};

export const otherIncomeConfig = {
    getRows: (space: SpaceData): string[][] => {
        const { operatingIncome: oi } = space.incomeExpenseProForma;

        return [
            createTableRow("CAM Recovery", "", "", formatCurrencyBlankUSD(oi.camRecovery)),
            createTableRow(
                "Utility Reimbursement Income",
                "",
                "",
                formatCurrencyBlankUSD(oi.utilityReimbursementIncome)
            ),
            createTableRow(
                "Miscellaneous Other Income",
                "",
                "",
                formatCurrencyBlankUSD(oi.miscellaneousOtherIncome)
            ),
            createTableRow(
                "Total Other Income",
                "",
                "",
                formatCurrencyBlankUSD(oi.totalOtherIncome)
            ),
        ];
    },
};

export const fixedExpenseConfig = {
    getRows: (space: SpaceData): string[][] => {
        const { fixedExpenses: fe, operatingIncome: oi } = space.incomeExpenseProForma;
        const egi = oi.effectiveGrossIncome;

        return [
            createTableRow(
                "Real Estate Taxes (In OAR)",
                "",
                formatPercentageBlank(fe.realEstateTaxes),
                formatCurrencyBlankUSD(fe.realEstateTaxes * egi * 0.01)
            ),
            createTableRow(
                "Insurance",
                "",
                formatPercentageBlank(fe.insurance),
                formatCurrencyBlankUSD(fe.insurance * egi * 0.01)
            ),
            createTableRow(
                "Professional Management",
                "",
                formatPercentageBlank(fe.professionalManagement),
                formatCurrencyBlankUSD(fe.professionalManagement * egi * 0.01)
            ),
            createTableRow(
                "On-Site Staffing",
                "",
                formatPercentageBlank(fe.onSiteStaffing),
                formatCurrencyBlankUSD(fe.onSiteStaffing * egi * 0.01)
            ),
            createTableRow(
                "Maintenance Staffing",
                "",
                formatPercentageBlank(fe.maintenanceStaffing),
                formatCurrencyBlankUSD(fe.maintenanceStaffing * egi * 0.01)
            ),
            createTableRow(
                "Subtotal",
                "",
                formatPercentageBlank(fe.subtotal),
                formatCurrencyBlankUSD(fe.subtotal * egi * 0.01)
            ),
        ];
    },
};

export const variableExpenseConfig = {
    getRows: (space: SpaceData): string[][] => {
        const { variableExpenses: ve, operatingIncome: oi } = space.incomeExpenseProForma;
        const egi = oi.effectiveGrossIncome;

        const createExpenseRow = (label: string, percentage: number) =>
            createTableRow(
                label,
                "",
                formatPercentageBlank(percentage),
                formatCurrencyBlankUSD(percentage * egi * 0.01)
            );

        return [
            createExpenseRow("Parking", ve.parking),
            createExpenseRow("Cleaning", ve.cleaning),
            createExpenseRow("Repairs And Maintenance", ve.repairsAndMaintenance),
            createExpenseRow("Elevator", ve.elevator),
            createExpenseRow("Utilities", ve.utilities),
            createExpenseRow("Security, Grounds", ve.securityGrounds),
            createExpenseRow("Administration & Management", ve.administrationAndManagement),
            createExpenseRow("CAM Expenses", ve.camExpenses),
            createExpenseRow("Subtotal", ve.subtotal),
        ];
    },
};

export const capitalExpenditureConfig = {
    getRows: (space: SpaceData): string[][] => {
        const { capitalExpenditure: ce, operatingIncome: oi } = space.incomeExpenseProForma;
        const egi = oi.effectiveGrossIncome;

        return [
            createTableRow(
                "Replacement Reserves",
                "",
                formatPercentageBlank(ce.replacementReserves),
                formatCurrencyBlankUSD(ce.replacementReserves * egi * 0.01)
            ),
            createTableRow(
                "Subtotal",
                "",
                formatPercentageBlank(ce.subtotal),
                formatCurrencyBlankUSD(ce.subtotal * egi * 0.01)
            ),
        ];
    },
};

export const capitalizationRateConfig = {
    getRows: (space: SpaceData): string[][] => {
        const { unloadedCap, etr } = space.incomeExpenseProForma;

        return [
            createTableRow("Unloaded Cap", "", formatPercentageBlank(unloadedCap), ""),
            createTableRow("ETR", "", formatPercentageBlank(etr), ""),
        ];
    },
};

export interface CostData {
    appealApproachCalculationData?: {
        cost?: {
            costApproachData?: {
                spaceData?: Record<string, Record<string, Array<{ pdfPageS3Keys?: string[] }>>>;
                featureData?: Record<string, Array<{ pdfPageS3Keys?: string[] }>>;
                siteImprovementData?: Record<string, Array<{ pdfPageS3Keys?: string[] }>>;
            };
        };
    };
}

export const getAllPdfPageS3Keys = (data: CostData): string[] => {
    const costApproachData = _.get(data, "appealApproachCalculationData.cost.costApproachData", {});
    const allS3Keys: string[] = [];

    const spaceData = costApproachData.spaceData || {};
    Object.keys(spaceData)
        .map(key => spaceData[key])
        .forEach(space => {
            Object.keys(space)
                .map(key => space[key])
                .forEach(typeData => {
                    typeData.forEach(item => {
                        const pdfKeys = item.pdfPageS3Keys || [];
                        allS3Keys.push(...pdfKeys);
                    });
                });
        });

    const featureData = costApproachData.featureData || {};
    Object.keys(featureData)
        .map(key => featureData[key])
        .forEach(feature => {
            feature.forEach(item => {
                const pdfKeys = item.pdfPageS3Keys || [];
                allS3Keys.push(...pdfKeys);
            });
        });

    const siteImprovementData = costApproachData.siteImprovementData || {};
    Object.keys(siteImprovementData)
        .map(key => siteImprovementData[key])
        .forEach(improvement => {
            improvement.forEach(item => {
                const pdfKeys = item.pdfPageS3Keys || [];
                allS3Keys.push(...pdfKeys);
            });
        });

    return allS3Keys;
};
